import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  ckConfirm,
  Panel,
  PanelGrid,
  RankList,
  SearchInput,
  Segmented,
  SimpleTable,
  Spinner,
  StatGrid,
  StatTile,
  Tag,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type BlastDto, type BlastReportDto, type RecipientRowDto } from "../../api/mailingApi";
import { useShell } from "../shell/ShellContext";
import { EmailPreview } from "./EmailBuilder";
import { BLAST_STATUS, MESSAGE_STATUS, blastWhen, errorMessage, fmtDateTime, fmtNum, pct } from "./mailingUi";

const PAGE = 50;

/** A launched blast: progress, opens / clicks, A/B result, links, recipients, and pause / resume / cancel. */
export function BlastReport({ blast: initial, onChanged }: { blast: BlastDto; onChanged: (b: BlastDto) => void }) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const { allowed } = useShell();
  const canSend = allowed("mailing.send");
  const canManage = allowed("mailing.manage");
  const [report, setReport] = useState<BlastReportDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<{ items: RecipientRowDto[]; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const load = useCallback(() => {
    mailingApi.blasts
      .report(initial.id)
      .then((r) => {
        setReport(r);
        setError(null);
      })
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את הדוח")));
  }, [initial.id]);
  useEffect(load, [load]);

  const blast = report?.blast ?? initial;
  const live = blast.status === "sending" || blast.status === "scheduled" || blast.status === "active";
  // a live blast refreshes itself
  useEffect(() => {
    if (!live) return undefined;
    const t = window.setInterval(load, 8000);
    return () => window.clearInterval(t);
  }, [live, load]);

  useEffect(() => {
    let alive = true;
    const t = window.setTimeout(() => {
      mailingApi.blasts
        .recipients(initial.id, { status: filter, q, page, pageSize: PAGE })
        .then((r) => alive && setRows(r))
        .catch(() => alive && setRows({ items: [], total: 0 }));
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [initial.id, filter, q, page, report?.blast.counts.sent]);

  const act = async (fn: () => Promise<BlastDto>, done: string) => {
    setBusy(true);
    try {
      const b = await fn();
      toast.success(done);
      onChanged(b);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "הפעולה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    const scheduled = blast.status === "scheduled";
    const ok = await ckConfirm({
      title: scheduled ? "לבטל את התזמון?" : "לעצור את הדיוור?",
      message: scheduled ? "הדיוור יחזור לטיוטה ולא יישלח." : "מי שעוד לא קיבל — לא יקבל. מה שכבר נשלח נשאר.",
      confirmLabel: scheduled ? "ביטול התזמון" : "עצירה",
      danger: !scheduled,
    });
    if (ok) await act(() => mailingApi.blasts.cancel(blast.id), scheduled ? "התזמון בוטל" : "הדיוור נעצר");
  };

  const duplicate = async () => {
    try {
      const copy = await mailingApi.blasts.duplicate(blast.id);
      navigate(`/mailing/${copy.id}`);
    } catch (e) {
      toast.error(errorMessage(e, "השכפול נכשל"));
    }
  };

  const c = blast.counts;
  const st = BLAST_STATUS[blast.status] ?? BLAST_STATUS.draft;
  const sent = c.sent || 0;

  return (
    <div className="ml-stack">
      <div className="ml-row ml-row--between">
        <div className="ml-row">
          <Tag tone={st.tone} icon={st.icon}>
            {st.label}
          </Tag>
          <span className="ml-muted">{blastWhen(blast)}</span>
          {blast.createdByName ? <span className="ml-muted">· {blast.createdByName}</span> : null}
        </div>
        <div className="ml-row">
          {canSend && blast.kind === "blast" && blast.status === "sending" ? (
            <Button icon="pause" onClick={() => void act(() => mailingApi.blasts.pause(blast.id), "הדיוור הושהה")} disabled={busy}>
              השהיה
            </Button>
          ) : null}
          {canSend && blast.kind === "blast" && blast.status === "paused" ? (
            <Button icon="play" variant="primary" onClick={() => void act(() => mailingApi.blasts.resume(blast.id), "השליחה חודשה")} disabled={busy}>
              המשך שליחה
            </Button>
          ) : null}
          {canSend && blast.kind === "blast" && ["scheduled", "sending", "paused"].includes(blast.status) ? (
            <Button icon="x" variant={blast.status === "scheduled" ? "default" : "danger"} onClick={() => void cancel()} disabled={busy}>
              {blast.status === "scheduled" ? "ביטול התזמון" : "עצירה"}
            </Button>
          ) : null}
          {canManage && blast.kind === "blast" ? (
            <Button icon="copy" onClick={() => void duplicate()}>
              שכפול
            </Button>
          ) : null}
          <Button icon="eye" variant="ghost" onClick={() => setShowContent((v) => !v)}>
            {showContent ? "הסתרת התוכן" : "התוכן שנשלח"}
          </Button>
          <Button icon="refresh" variant="ghost" onClick={load}>
            רענון
          </Button>
        </div>
      </div>

      {blast.lastError ? <Banner tone="warning">{blast.lastError}</Banner> : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <StatGrid min={150}>
        <StatTile label="נמענים" icon="users" tone="neutral" value={fmtNum(c.recipients)} sub={c.suppressed ? `${fmtNum(c.suppressed)} חסומים לא נכללו` : undefined} />
        <StatTile label="נשלחו" icon="mail" tone="primary" value={fmtNum(sent)} sub={c.recipients ? pct(sent, c.recipients) : undefined} />
        <StatTile label="נפתחו" icon="eye" tone="success" value={fmtNum(c.opened)} sub={pct(c.opened, sent)} onClick={() => setFilter("opened")} active={filter === "opened"} />
        <StatTile label="הקליקו" icon="link" tone="violet" value={fmtNum(c.clicked)} sub={pct(c.clicked, sent)} onClick={() => setFilter("clicked")} active={filter === "clicked"} />
        <StatTile label="הסירו את עצמם" icon="x" tone="neutral" value={fmtNum(c.unsubscribed)} sub={pct(c.unsubscribed, sent)} onClick={() => setFilter("unsubscribed")} active={filter === "unsubscribed"} />
        <StatTile
          label="חזרו / נכשלו"
          icon="alert"
          tone="danger"
          warn={c.bounced + c.failed + c.complained > 0}
          value={fmtNum(c.bounced + c.failed + c.complained)}
          sub={pct(c.bounced + c.failed, sent || c.recipients)}
          onClick={() => setFilter("bounced")}
          active={filter === "bounced"}
        />
      </StatGrid>

      {showContent ? (
        <Panel title={blast.subject ?? ""} icon="mail" sub={blast.preheader ?? undefined}>
          <EmailPreview html={blast.html ?? null} />
        </Panel>
      ) : null}

      <PanelGrid cols={2}>
        {report?.ab ? (
          <Panel title="בדיקת A/B" icon="sliders" sub={blast.ab.winner ? `המנצח: ${blast.ab.winner}` : `ההכרעה אחרי ${blast.ab.waitMinutes / 60} שעות`}>
            <SimpleTable
              ariaLabel="תוצאות A/B"
              rows={[
                { key: "A", subject: blast.subject, ...report.ab.A },
                { key: "B", subject: blast.ab.subjectB, ...report.ab.B },
              ]}
              rowKey="key"
              columns={[
                { key: "key", label: "", render: (r: { key: string }) => <b>{r.key}{blast.ab.winner === r.key ? " ✓" : ""}</b> },
                { key: "subject", label: "נושא" },
                { key: "sent", label: "נשלחו", align: "end", render: (r: { sent: number }) => fmtNum(r.sent) },
                { key: "opened", label: "פתיחות", align: "end", render: (r: { sent: number; opened: number }) => pct(r.opened, r.sent) },
                { key: "clicked", label: "הקלקות", align: "end", render: (r: { sent: number; clicked: number }) => pct(r.clicked, r.sent) },
              ]}
            />
          </Panel>
        ) : null}
        <Panel title="קישורים שהקליקו עליהם" icon="link" flush>
          <RankList
            loading={!report}
            items={(report?.links ?? []).map((l) => ({ key: l.link, label: <span dir="ltr" className="ml-ellipsis">{l.link}</span>, value: l.people, sub: `${fmtNum(l.clicks)} הקלקות` }))}
            formatValue={(v) => `${fmtNum(v)} אנשים`}
            empty="עדיין אין הקלקות"
          />
        </Panel>
      </PanelGrid>

      <Panel title="נמענים" icon="users" flush>
        <div className="ml-table-tools">
          <Segmented
            ariaLabel="סינון"
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setPage(1);
            }}
            items={[
              { value: "", label: "הכול" },
              { value: "sent", label: "נשלחו" },
              { value: "opened", label: "פתחו" },
              { value: "clicked", label: "הקליקו" },
              { value: "bounced", label: "חזרו" },
              { value: "failed", label: "נכשלו" },
              { value: "unsubscribed", label: "הוסרו" },
              { value: "suppressed", label: "חסומים" },
            ]}
          />
          <SearchInput
            value={q}
            onChange={(v) => {
              setQ(v);
              setPage(1);
            }}
            placeholder="חיפוש לפי שם או מייל…"
          />
        </div>
        <SimpleTable
          ariaLabel="נמענים"
          loading={!rows}
          rows={rows?.items ?? []}
          rowKey="id"
          empty="אין נמענים בסינון הזה"
          columns={[
            { key: "name", label: "שם", render: (r: RecipientRowDto) => r.name || "—" },
            { key: "email", label: "מייל", ltr: true },
            {
              key: "status",
              label: "מצב",
              render: (r: RecipientRowDto) => {
                const m = MESSAGE_STATUS[r.status] ?? { label: r.status, tone: "neutral" as const };
                return (
                  <span title={r.error ?? undefined}>
                    <Tag tone={m.tone}>{m.label}</Tag>
                    {r.variant ? <span className="ml-muted"> · {r.variant}</span> : null}
                  </span>
                );
              },
            },
            { key: "sentAt", label: "נשלח", render: (r: RecipientRowDto) => fmtDateTime(r.sentAt) },
            { key: "openedAt", label: "נפתח", render: (r: RecipientRowDto) => (r.openedAt ? `${fmtDateTime(r.openedAt)}${r.openCount > 1 ? ` (×${r.openCount})` : ""}` : "") },
            { key: "clickedAt", label: "הקליק", render: (r: RecipientRowDto) => (r.clickedAt ? `${fmtDateTime(r.clickedAt)}${r.clickCount > 1 ? ` (×${r.clickCount})` : ""}` : "") },
          ]}
        />
        {rows && rows.total > PAGE ? (
          <div className="ml-row ml-row--center ml-pager">
            <Button size="sm" icon="right" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              הקודם
            </Button>
            <span className="ml-muted">
              {fmtNum((page - 1) * PAGE + 1)}–{fmtNum(Math.min(page * PAGE, rows.total))} מתוך {fmtNum(rows.total)}
            </span>
            <Button size="sm" icon="left" disabled={page * PAGE >= rows.total} onClick={() => setPage((p) => p + 1)}>
              הבא
            </Button>
          </div>
        ) : null}
      </Panel>
      {!report && !error ? (
        <div className="ml-busy">
          <Spinner size={24} />
        </div>
      ) : null}
    </div>
  );
}
